/**
 * Custom hook for WebSocket-based video processing progress
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { WS_URL, videoAPI } from '../utils/videoUtils';

/**
 * Hook for tracking video job progress via WebSocket
 * @param {string} jobId - The job ID to track
 * @param {Object} options - Options
 * @returns {Object} - Progress state and controls
 */
export function useVideoProgress(jobId, options = {}) {
  const {
    onComplete = () => {},
    onError = () => {},
    pollInterval = 1000,
    autoConnect = true,
    jobType = 'detection', // 'detection' or 'processing' - determines which API endpoint to use
  } = options;

  const [progress, setProgress] = useState(0);
  const [eta, setEta] = useState(0);
  const [step, setStep] = useState('');
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Use refs for callbacks to prevent re-renders from recreating connections
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const completedRef = useRef(false); // Track if we already called onComplete

  // Update refs when callbacks change
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  // Cleanup function
  const cleanup = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Reset completed flag when jobId changes
  useEffect(() => {
    completedRef.current = false;
  }, [jobId]);

  // Use a ref for status to avoid stale closure in ws.onclose
  const statusRef = useRef(status);
  statusRef.current = status;

  // Ref for connect function to avoid circular dependency
  const connectRef = useRef(null);

  // Polling fallback - defined first to avoid circular dependency
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return;

    const poll = async () => {
      if (!jobId) return;
      // Don't poll if already completed
      if (completedRef.current) return;

      try {
        // Use the correct endpoint based on job type
        // Detection jobs need getDetectionStatus (returns persons array)
        // Processing jobs need getProcessingStatus (returns preview_url/download_url)
        let data;
        if (jobType === 'processing') {
          data = await videoAPI.getProcessingStatus(jobId);
        } else {
          data = await videoAPI.getDetectionStatus(jobId);
        }

        setProgress(data.progress_percent || 0);
        setEta(data.eta_seconds || 0);
        setStep(data.current_step || '');
        setStatus(data.status || 'processing');

        if (data.status === 'completed') {
          // Only call onComplete once
          if (!completedRef.current) {
            completedRef.current = true;
            setResult(data);
            onCompleteRef.current(data);
            cleanup();
          }
        } else if (data.status === 'failed') {
          setError(data.error || 'Processing failed');
          onErrorRef.current(data.error || 'Processing failed');
          cleanup();
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    };

    poll(); // Initial poll
    pollIntervalRef.current = setInterval(poll, pollInterval);
  }, [jobId, pollInterval, cleanup]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!jobId) return;

    cleanup();

    try {
      const ws = new WebSocket(`${WS_URL}/ws/progress/${jobId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'progress') {
            setProgress(data.percent || 0);
            setEta(data.eta_seconds || 0);
            setStep(data.current_step || '');
            setStatus(data.status || 'processing');
          } else if (data.type === 'complete') {
            // Only call onComplete once
            if (!completedRef.current) {
              completedRef.current = true;
              setProgress(100);
              setStatus('completed');
              setResult(data);
              onCompleteRef.current(data);
              cleanup();
            }
          } else if (data.type === 'error') {
            setError(data.message);
            setStatus('failed');
            onErrorRef.current(data.message);
            cleanup();
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onerror = () => {
        setIsConnected(false);
        // Fall back to polling
        startPolling();
      };

      ws.onclose = () => {
        setIsConnected(false);
        // Don't reconnect if already completed
        if (completedRef.current) return;
        // Reconnect if not completed
        if (statusRef.current !== 'completed' && statusRef.current !== 'failed') {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (connectRef.current) connectRef.current();
          }, 3000);
        }
      };
    } catch (e) {
      console.error('WebSocket connection failed:', e);
      // Fall back to polling
      startPolling();
    }
  }, [jobId, cleanup, startPolling]);

  // Update connect ref
  connectRef.current = connect;

  // Auto-connect on mount - only when jobId changes
  // Use polling directly since WebSocket notifications aren't implemented for processing
  useEffect(() => {
    if (autoConnect && jobId) {
      // Start polling immediately - more reliable than WebSocket for now
      startPolling();
    }

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, autoConnect]);

  // Send ping to keep connection alive
  useEffect(() => {
    if (!isConnected || !wsRef.current) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  return {
    progress,
    eta,
    step,
    status,
    error,
    result,
    isConnected,
    isProcessing: status === 'processing' || status === 'queued',
    isComplete: status === 'completed',
    isFailed: status === 'failed',
    connect,
    disconnect: cleanup,
  };
}

/**
 * Hook for managing video detection job
 */
export function useVideoDetection() {
  const [jobId, setJobId] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const startDetection = useCallback(async (sessionId, fileId, enableGender = true, startSeconds = 0, endSeconds = 0) => {
    setIsStarting(true);
    setStartError(null);

    try {
      const result = await videoAPI.startDetection(sessionId, fileId, enableGender, startSeconds, endSeconds);
      setJobId(result.job_id);
      return result;
    } catch (e) {
      setStartError(e.message);
      throw e;
    } finally {
      setIsStarting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setJobId(null);
    setStartError(null);
  }, []);

  return {
    jobId,
    isStarting,
    startError,
    startDetection,
    reset,
  };
}

/**
 * Hook for managing video processing job
 */
export function useVideoProcessing() {
  const [jobId, setJobId] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const startProcessing = useCallback(async (sessionId, fileId, blurSettings) => {
    setIsStarting(true);
    setStartError(null);

    try {
      const result = await videoAPI.startProcessing(sessionId, fileId, blurSettings);
      setJobId(result.job_id);
      return result;
    } catch (e) {
      setStartError(e.message);
      throw e;
    } finally {
      setIsStarting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setJobId(null);
    setStartError(null);
  }, []);

  return {
    jobId,
    isStarting,
    startError,
    startProcessing,
    reset,
  };
}

export default useVideoProgress;
